/** Substrings that suggest a numeric/string value is a date/time (GIS field names vary a lot). */
const DATE_TIME_FIELD_HINTS = [
  'date',
  'time',
  'dtg',
  '_at',
  'created',
  'updated',
  'modified',
  'effective',
  'expires',
  'timestamp',
] as const;

/** Contain "time" (or similar) but are not date fields — avoid matching via `includes('time')`. */
const DATE_TIME_TIME_FALSE_POSITIVE_SUBSTRINGS = [
  'runtime',
  'lifetime',
  'timeout',
  'sometime',
  'anytime',
] as const;

/** Legacy short names that are date-ish but do not contain the usual substrings. */
const DATE_TIME_SHORT_NAMES = /^(start|end)(_)?$/i;

function fieldNameLooksLikeDateTime(name: string): boolean {
  const lower = name.toLowerCase();
  if (DATE_TIME_SHORT_NAMES.test(name.trim())) return true;
  const hasHint = DATE_TIME_FIELD_HINTS.some(hint => lower.includes(hint));
  if (!hasHint) return false;
  const looksLikeTimeNoise = DATE_TIME_TIME_FALSE_POSITIVE_SUBSTRINGS.some(s =>
    lower.includes(s),
  );
  if (looksLikeTimeNoise) {
    return (
      lower.includes('date') ||
      lower.includes('timestamp') ||
      lower.includes('dtg')
    );
  }
  return true;
}

export const formatValue = (value: unknown, fieldName?: string): string => {
  if (value === null || value === undefined) return '-';

  const isDateTimeField = fieldName && fieldNameLooksLikeDateTime(fieldName);

  if (
    isDateTimeField &&
    (typeof value === 'string' || typeof value === 'number')
  ) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString();
    }
  }

  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
};

/** Normalizes GIS field keys for comparison (underscores, camelCase). */
export const normalizeGisFieldName = (fieldName: string): string => {
  if (!fieldName) return '';
  const withSpaces = fieldName
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  return withSpaces.trim().toLowerCase().replace(/\s+/g, ' ');
};

/** True when the field name contains the word `video` (after GIS normalization). */
export const fieldNameHasVideoWord = (fieldName: string): boolean => {
  if (!fieldName) return false;
  return /\bvideo\b/.test(normalizeGisFieldName(fieldName));
};

const HTTP_STREAM_URL_RE = /^https?:\/\/.+/i;
const HLS_URL_EXT = /\.(m3u8)($|\?)/i;
const FLV_URL_EXT = /\.(flv)($|\?)/i;

/** Returns an http(s) URL string if `value` looks like a stream URL; otherwise null. */
export function parseVideoStreamUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || !HTTP_STREAM_URL_RE.test(trimmed)) return null;
  return trimmed;
}

/** Hint HLS/FLV for VideoPlayer when the URL has no obvious extension. */
export function inferVideoPlayerFormat(
  url: string,
  fieldName: string,
): 'hls' | 'flv' | '' {
  const n = normalizeGisFieldName(fieldName);
  if (HLS_URL_EXT.test(url) || n.includes('hls')) return 'hls';
  if (FLV_URL_EXT.test(url) || n.includes('flv')) return 'flv';
  return '';
}

/** Fields omitted from click-to-identify popover (matched on normalized name). */
const CLICK_TO_IDENTIFY_HIDDEN_FIELDS = new Set([
  'video rtsp uri',
  'token',
  'is blocked',
  'snapshot address',
  'is video authentication required',
  'camera status',
  'snapshot status',
  'objectid',
]);

export const isClickToIdentifyHiddenField = (fieldName: string): boolean =>
  CLICK_TO_IDENTIFY_HIDDEN_FIELDS.has(normalizeGisFieldName(fieldName));

export const formatFieldName = (fieldName: string): string => {
  if (!fieldName) return '';

  // Replace underscores with spaces first
  const withSpaces = fieldName.replace(/_/g, ' ');
  // Insert spaces before camelCase boundaries and between acronym + word
  // Examples: "camelCase" -> "camel Case", "HTTPServer" -> "HTTP Server"
  const splitCamel = withSpaces
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

  const words = splitCamel.split(' ');
  const formattedWords = words.map(word => {
    if (!word) return '';
    // Preserve all-caps acronyms (>=2 chars) as-is
    if (/^[A-Z]{2,}$/.test(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return formattedWords.join(' ');
};

export const formatCoordinate = (coord: number | null | undefined): string => {
  if (coord === null || coord === undefined) return '-';
  return typeof coord === 'number' ? coord.toFixed(6) : '-';
};
